import { PERMISSION_TYPE } from 'hooks/useRBAC';

import { PERMISSIONS } from '../auth/constants';

export enum ROUTING_RULESET_KEYS {
  ALL = 'all-routing-rulesets',
  CASHIER_PAYMENT_METHODS = 'cashier-payment-methods',
  MERGE_OPERATION = 'merge-operation',
  MERGE_OPERATION_DETAILS = 'merge-operation-details',
  OPERATORS = 'routing-ruleset-operators',
  PAYMENT_METHODS = 'payment-methods',
  RULESET = 'routing-ruleset',
  TERMINAL_LINKS = 'terminal-links',
  TRUSTED_WITHOUT_MIN_STATS = 'routing-ruleset-trusted-without-min-stats',
}

export enum ROUTING_RULESET_OPERATIONS {
  BETWEEN = 'bw',
  EQUALS = 'eq',
  GREATER_THAN = 'gt',
  GREATER_THAN_OR_EQUAL_TO = 'ge',
  IN = 'in',
  LESS_THAN = 'lt',
  LESS_THAN_OR_EQUALS_TO = 'le',
  NOT_BETWEEN = 'nb',
  NOT_EQUALS = 'ne',
  NOT_IN = 'ni',
}

export interface IFormPermissions {
  CONDITION_TYPE: PERMISSION_TYPE;
  PERMISSIONS: PERMISSIONS[];
}

export const ROUTING_RULESET_OPERATION_KEYS: Record<string, string> = {
  [ROUTING_RULESET_OPERATIONS.GREATER_THAN]: '>',
  [ROUTING_RULESET_OPERATIONS.GREATER_THAN_OR_EQUAL_TO]: '>=',
  [ROUTING_RULESET_OPERATIONS.LESS_THAN]: '<',
  [ROUTING_RULESET_OPERATIONS.LESS_THAN_OR_EQUALS_TO]: '<=',
};

/**
 * Read↔write field-name contract for rulesets / change rules.
 *
 * The list GET returns a flattened read model whose names cannot be derived
 * from the write keys (POST/PUT nested body, PATCH dotted paths) by any single
 * rule — prefixes are dropped (`bin.cardType` → `cardType`), swapped
 * (`stats.dpCount` → `customerDpCount`) or flattened (`bin.range.value` →
 * `binRangeValue`), and one leaf is renamed (`bin.issuingOrg.valuesList` →
 * `binIssuingOrgValue`). This table is the single FE source of truth:
 * read name (grid dataKey) → write path. Names absent here are identical on
 * both sides.
 *
 * Not expressible as entries: the list's `metadata.*` maps to
 * `transaction.metadata.*` on PUT/POST but to a literal `metadata` key on the
 * group-edit PATCH, and change rules read `fromCpm`/`toCpm` objects while
 * writing `fromCpmCode`/`toCpmCode` strings (bridged in
 * `api/changeRule/normalizers.ts`).
 */
export const READ_TO_WRITE_FIELD_PATHS: Record<string, string> = {
  billingInfo: 'bin.billingInfo',
  binIssuingOrgOperation: 'bin.issuingOrg.operation',
  binIssuingOrgValue: 'bin.issuingOrg.valuesList',
  binRangeOperation: 'bin.range.operation',
  binRangeValue: 'bin.range.value',
  cardBrands: 'bin.cardBrands',
  cardType: 'bin.cardType',
  customerCcDpAmount: 'stats.ccDpAmount',
  customerCcDpCount: 'stats.ccDpCount',
  customerCcWdAmount: 'stats.ccWdAmount',
  customerCcWdCount: 'stats.ccWdCount',
  customerDaysFromFtd: 'customer.daysFromFtd',
  customerDaysFromReg: 'customer.daysFromReg',
  customerDpAmount: 'stats.dpAmount',
  customerDpCount: 'stats.dpCount',
  customerMaxCcDpAmount: 'maxStats.ccDpAmount',
  customerMaxCcDpCount: 'maxStats.ccDpCount',
  customerMaxCcWdAmount: 'maxStats.ccWdAmount',
  customerMaxCcWdCount: 'maxStats.ccWdCount',
  customerMaxDpAmount: 'maxStats.dpAmount',
  customerMaxDpCount: 'maxStats.dpCount',
  customerMaxUccDpAmount: 'maxStats.uccDpAmount',
  customerMaxUccDpCount: 'maxStats.uccDpCount',
  customerMaxUccWdAmount: 'maxStats.uccWdAmount',
  customerMaxUccWdCount: 'maxStats.uccWdCount',
  customerMaxWdAmount: 'maxStats.wdAmount',
  customerMaxWdCount: 'maxStats.wdCount',
  customerRegBeforeDate: 'customer.regBeforeDate',
  customerUccDpAmount: 'stats.uccDpAmount',
  customerUccDpCount: 'stats.uccDpCount',
  customerUccWdAmount: 'stats.uccWdAmount',
  customerUccWdCount: 'stats.uccWdCount',
  customerWdAmount: 'stats.wdAmount',
  customerWdCount: 'stats.wdCount',
  excludedFromCascading: 'transaction.excludedFromCascading',
  phonePrefixOperation: 'customer.phonePrefix.operation',
  phonePrefixValue: 'customer.phonePrefix.value',
  transactionType: 'transaction.type',
};

export const UNLIMITED = 31556952000;

export const MAX_TRUSTED_FILTER_ITEMS = 100;

export const INITIAL_ON_COLUMNS_RULESETS = [
  'checkboxes',
  'merchantName',
  'name',
  'merchantTerminalName',
  'cashierPaymentMethodName',
  'terminalLinkDescription',
  'priority',
  'ratio',
  'enabled',
  'commissionOverride',
  'transactionType',
  'compositeAmount',
  'dPCount',
  'txCurrency',
  'compositeTxAmount',
  'compositeCustomerCountry',
  'compositeCustomerCountryGroup',
  'binCountry',
  'binRangeOperation',
  'binRangeValue',
  'binIssuingOrgOperation',
  'binIssuingOrgValue',
  'cardNetwork',
  'paymentProviderCurrency',
  'useCurrencyConversion',
  'schedule',
  'paymentMethod',
];

export const MAIN_COLUMNS = ['name'];

export const DEFAULT_LIKES_FIELDS = ['name', 'terminalLink.description'];

export const BETWEEN_LIST_RULESETS = new Set([
  'customerCcDpAmount',
  'customerCcDpCount',
  'customerCcWdAmount',
  'customerCcWdCount',
  'customerDpAmount',
  'customerUccDpAmount',
  'customerUccDpCount',
  'customerUccWdAmount',
  'customerUccWdCount',
  'customerWdAmount',
  'customerWdCount',
  'exchangeRateAdjustment',
]);

export const UNLIMITED_HOURS_LIST_RULESETS = new Set([
  'timerConfig.successValidityPeriod',
]);
export const HOURS_LIST_RULESETS = new Set(['timerConfig.failRecoveryPeriod']);

export const UPDATE_RULESET_PAGE: IFormPermissions = {
  CONDITION_TYPE: PERMISSION_TYPE.AND,
  PERMISSIONS: [
    PERMISSIONS.CAN_UPDATE_RULESET,
    PERMISSIONS.CAN_VIEW_RULESETS,
    PERMISSIONS.CAN_VIEW_MERCHANTS,
  ],
};
